import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';

import { getGroup, listGroupMembers, validateApiResponse } from '../../../services';
import { GROUP_MEMBERS_PAGE_SIZE } from '../constants/groups';
import { mapApiGroupMemberToUi, mapApiGroupToUi } from '../utils/groupMappers';

const NOT_FOUND = 404;
const EMPTY = { key: null, group: null, members: [], error: null, notFound: false };
const NO_PENDING = Object.freeze([]);

/**
 * One group (header data) and its members (table). `isLoading` is derived: the last settled
 * request is not the one for the current group/reload.
 *
 * Members being removed (inside their Undo window) are kept apart as `pending` ids and filtered out
 * of `members` and out of the header count at read time, so a reload, an edit that replaces the
 * group, or an Undo cannot bring a removed row back or misplace it: the server list keeps its order.
 *
 * @param {string} groupId
 * @returns {{ group: object|null, members: object[], isLoading: boolean, error: string|null,
 *   notFound: boolean, reload: () => void, applyGroup: (group: object) => void,
 *   hideMember: (id: string) => void, showMember: (id: string) => void,
 *   dropMember: (id: string) => void }}
 */
export const useGroup = (groupId) => {
  const { t } = useTranslation('people-access');
  const [reloadToken, setReloadToken] = useState(0);
  const [settled, setSettled] = useState(EMPTY);
  const [pending, setPending] = useState(NO_PENDING);
  const fetchIdRef = useRef(0);
  const requestKey = `${reloadToken}|${groupId}`;

  useEffect(() => {
    const fetchId = ++fetchIdRef.current;
    let mounted = true;
    const isCurrent = () => mounted && fetchId === fetchIdRef.current;
    const loadError = t('groups.detail.loadError');

    Promise.all([
      getGroup(groupId),
      listGroupMembers(groupId, { page_size: GROUP_MEMBERS_PAGE_SIZE }),
    ])
      .then(([groupRes, membersRes]) => {
        if (groupRes?.status_code === NOT_FOUND) {
          return { ...EMPTY, key: requestKey, notFound: true };
        }
        const { body: groupBody } = validateApiResponse(groupRes, loadError);
        const { body: membersBody } = validateApiResponse(membersRes, loadError);
        return {
          ...EMPTY,
          key: requestKey,
          group: mapApiGroupToUi(groupBody),
          members: (membersBody?.items ?? []).map(mapApiGroupMemberToUi),
        };
      })
      .catch((err) => ({ ...EMPTY, key: requestKey, error: err?.message ?? loadError }))
      .then((next) => {
        if (isCurrent()) setSettled(next);
      });

    return () => {
      mounted = false;
    };
  }, [groupId, reloadToken, requestKey, t]);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);
  /** Replaces the header data with a mutation response, without refetching the members. */
  const applyGroup = useCallback((next) => setSettled((prev) => ({ ...prev, group: next })), []);

  /** Starts a removal: the member leaves the table and the count at once (Undo window). */
  const hideMember = useCallback(
    (id) => setPending((prev) => (prev.includes(id) ? prev : [...prev, id])),
    [],
  );
  /** Undo or failed removal: the member is back, where the server list had them. */
  const showMember = useCallback((id) => setPending((prev) => prev.filter((p) => p !== id)), []);
  /** The removal was committed: the member is gone for good (table, count and pending). */
  const dropMember = useCallback((id) => {
    setSettled((prev) => {
      if (!prev.members.some((m) => m.id === id)) return prev;
      return {
        ...prev,
        members: prev.members.filter((m) => m.id !== id),
        group: prev.group && { ...prev.group, memberCount: Math.max(0, prev.group.memberCount - 1) },
      };
    });
    setPending((prev) => prev.filter((p) => p !== id));
  }, []);

  const { error, notFound } = settled;
  const isLoading = settled.key !== requestKey;
  const members = useMemo(
    () => (pending.length ? settled.members.filter((m) => !pending.includes(m.id)) : settled.members),
    [settled.members, pending],
  );
  const group = useMemo(() => {
    if (!settled.group) return settled.group;
    const hidden = settled.members.length - members.length;
    return hidden ? { ...settled.group, memberCount: Math.max(0, settled.group.memberCount - hidden) } : settled.group;
  }, [settled.group, settled.members, members]);

  return useMemo(
    () => ({
      group,
      members,
      isLoading,
      error,
      notFound,
      reload,
      applyGroup,
      hideMember,
      showMember,
      dropMember,
    }),
    [group, members, isLoading, error, notFound, reload, applyGroup, hideMember, showMember, dropMember],
  );
};
